import { type FortuneWheelPrize } from './fortune-wheel'
import { type Nullable } from './common'
import { CHALLENGE_PRIZE_TYPE, MINI_GAME_ID, MYSTERY_BOX_TYPE } from './player-config'

export enum SPECIAL_CURRENCY_ID {
  GOLD_FEATHER = 'goldFeather',
  GOLDEN_EGG = 'goldenEgg',
  TOP_HAT = 'topHat',
  CROWN = 'crown',
  PLANT = 'plant',
  KEY = 'key',
  GEM = 'gem',
  PARACHUTE = 'parachute',
  HOLIDAY_ORB = 'holidayOrb'
}

export enum DAILY_TASK_ID {
  TAP = 'tap',
  UPGRADE = 'upgrade',
  POKE = 'poke',
  SHARE_TO_STORY = 'shareToStory',
  SPIN_THE_WHEEL = 'spinTheWheel',
  TON_DAILY_CHECKIN_LEGACY = 'crackTheEgg',
  TON_DAILY_CHECKIN = 'tonDailyCheckin',
  BUY_STARS = 'buyStars',
  DAPP_RADAR_TTM = 'dappRadarTtm',
  DAILY_PURCHASE = 'dailyPurchase',
  ALL_DONE = 'allDone',
}

export enum ACTIVITY_ID {
  WATCH_AD_FOR_DOUBLE_OFFLINE_PROFIT = 'watchAdForDoubleOfflineProfit',
  WATCH_AD_FOR_SPIN = 'watchAdForSpin',
  WATCH_AD_INTERSTITIAL = 'watchAdInterstitial',
  WATCH_AD_FOR_DOUBLE_PRIZE = 'watchAdForDoublePrize',
}

export type PlayerActivities = {
  date: string
  [ACTIVITY_ID.WATCH_AD_FOR_DOUBLE_OFFLINE_PROFIT]: number
  [ACTIVITY_ID.WATCH_AD_FOR_SPIN]: number
  [ACTIVITY_ID.WATCH_AD_INTERSTITIAL]: number
  [ACTIVITY_ID.WATCH_AD_FOR_DOUBLE_PRIZE]: number
  [DAILY_TASK_ID.TAP]: number
  [DAILY_TASK_ID.UPGRADE]: number
  [DAILY_TASK_ID.BUY_STARS]: number
  [DAILY_TASK_ID.POKE]: boolean
  [DAILY_TASK_ID.DAILY_PURCHASE]: boolean
  [DAILY_TASK_ID.SPIN_THE_WHEEL]: boolean
}

export type PlayerDailyClaims = {
  date: string
  claimed: Partial<Record<DAILY_TASK_ID, boolean>>
}

export type PlayerStats = {
  config: { playerId: number, referrerId: Nullable<number>, season: string }
  coins: number
  specialCurrencies: Partial<Record<SPECIAL_CURRENCY_ID, number>>
  pathwayEvents: {
    instanceNumbers: Record<string, number>
    runningEvents: Record<string, { startTimestamp: number, duration: number, currentStep: number }>
    completionsCount: number
    stepCompletionsCount: number
  }
  rewardLiveOps: {
    runningEvents: Record<number, { startTimestamp: number, completedSteps: number[], claimed: boolean, endTimestamp: number }>
    completionsCount: number
    stepCompletionsCount: number
  }
  profit: number
  level: number
  countryCode: string
  token?: string
  lastOfflineProfit?: number
  temporaryHasDoneTxV2?: boolean
  experience: number
  lastUpdate: number
  referrals: {
    count: number
    lastReferral?: number
  }
  friends: {
    count: number
    lastFriend?: number
    virtualCount: number
  }
  tap: {
    level: number
    earnPerTap: number
    coinPerUnit: number
  }
  energy: {
    level: number
    max: number
    available: number
  }
  refillBooster: {
    max: number
    available: number
    firstUseTimestamp: number
    lastUseTimestamp: number
  }
  upgrades: Record<string, { level: number, profit: number, lastUpgrade: number }>
  upgradeCount: number
  challenges: {
    dailyQuestion?: { lastDone: string }
    dailyCipher?: { lastDone: string }
    dailyCombo?: { currentDate: string, foundCombo: string[], lastReward?: number }
    completionsCount: {
      dailyCombo: number
    }
  }
  activities?: PlayerActivities
  tasks: {
    counters: Record<string, number>
    lastDone: Record<string, number>
    dailyTasks?: PlayerDailyClaims
    tonDailyCheckinCredits: number
    dappRadarTtmCredits: number
    dailyCheckin: {
      lastCheckin: string
      consecutiveDays: number
      maxConsecutiveDays?: number
    }
    others: Record<string, {
      isDone?: boolean,
      actionCompletedAt?: number,
      reward?: number,
      isVerified?: boolean,
      nextRepeatTimestamp?: number
    }>
  }
  onboarding: {
    done: boolean
  }
  enabledFeatures: Partial<{
    offlineProfit: { until: number, amount: number }
    doubleProfit: { until: number }
  }>
  featureFlags: Record<string, boolean>
  reminders: Record<string, number>
  fortuneWheel: {
    prizes: {
      freeUpgrades: number
    }
    lastPrize?: FortuneWheelPrize
    available: number
    lastDailyTicketTimestamp: number
  }
  settings: {
    tonWalletAddress?: string | null
    notifications: {
      energyRefilled: boolean
      poke: boolean
      offlineProfit: boolean
      referral: boolean
    }
  }
  simpleActions: { claimedDoublePrizeByAdd?: { completedAt: number } } & Record<string, { completedAt: number }>
  gifts: Partial<Record<GIFT_ID, { claimedAt: number }>>
  ecommerce: {
    claimedRewards?: number[]
    tonTxCountClaimedRewards?: number[]
    pathwayCountClaimedRewards?: number[]
    totalSpentCents: number
    tonTransactionsCount: number
    starsBalance: number
    mysteryBoxes: Record<MYSTERY_BOX_TYPE, number>
    revealedMysteryBoxes: number
    oneTimePurchases: Record<string, { purchasedAt: number }>
    lastPurchases: Record<string, { purchasedAt: number }>
    collectibles: {
      ownedIds: string[]
      tap: {
        currentSkinId: Nullable<string>
      }
    }
  }
  inventory: Record<INVENTORY_ID, number>
  migrationVersion: number
  miniGameCompletions: Partial<Record<MINI_GAME_ID, number>>
  specialCurrenciesLastDailyTimestamp: Partial<Record<SPECIAL_CURRENCY_ID, number>>
  isPresaleSubscribed?: boolean
}

export enum GIFT_ID {
  STARTER_PACK = 'starterPack',
}

export enum BADGE_ID {
  ALPHA = 'alpha',
  BETA = 'beta',
  DAILY_CARD_1 = 'dailyCard1',
  DAILY_CARD_2 = 'dailyCard2',
  DAILY_CARD_3 = 'dailyCard3',
  DISCORD_OG = 'discordOg',
  WEEKLY_CARD_1 = 'weeklyCard1',
  WEEKLY_CARD_2 = 'weeklyCard2',
  WEEKLY_CARD_3 = 'weeklyCard3',
  ALL_TIME_CARD_1 = 'allTimeCard1',
  ALL_TIME_CARD_2 = 'allTimeCard2',
  ALL_TIME_CARD_3 = 'allTimeCard3',
  ALL_TIME_CARD_4 = 'allTimeCard4',
  CRYPTO_PATRON = 'cryptoPatron',
  BLOCKCHAIN_SPONSOR = 'blockchainSponsor',
  DEFI_ANGEL = 'defiAngel',
  TOKEN_MOGUL = 'tokenMogul',
  BLOCKCHAIN_PATHFINDER = 'blockchainPathfinder',
  BLOCK_BUILDER = 'blockBuilder',
  CRYPTO_CRUSADER = 'cryptoCrusader',
  ONCHAIN_CHAMPION = 'onchainChampion',
  ONCHAIN_TITAN = 'onchainTitan',
  GRAND_PATRON = 'grandPatron',
  ECOSYSTEM_GUARDIAN = 'ecosystemGuardian',
  PATHWAY_EXPLORER = 'pathwayExplorer',
  PATHWAY_CONQUEROR = 'pathwayConqueror',
  PATHWAY_LEGEND = 'pathwayLegend',
  EASTER_HUNT_CHAMPION = 'easterHuntChampion',
  EASTER_HUNT_LEGEND = 'easterHuntLegend',
  EASTER_HUNT_PRO = 'easterHuntPro',
  EASTER_HUNT_MASTER = 'easterHuntMaster',
  PLANT_PATHWAY_CHAMPION = 'plantPathwayChampion',
  PLANT_PATHWAY_LEGEND = 'plantPathwayLegend',
  PLANT_PATHWAY_PRO = 'plantPathwayPro',
  PLANT_PATHWAY_MASTER = 'plantPathwayMaster',
  KEY_PATHWAY_CHAMPION = 'keyPathwayChampion',
  KEY_PATHWAY_LEGEND = 'keyPathwayLegend',
  KEY_PATHWAY_PRO = 'keyPathwayPro',
  KEY_PATHWAY_MASTER = 'keyPathwayMaster',
  PATHWAY_CHALLENGE1 = 'pathwayChallenge1',
  PATHWAY_CHALLENGE2 = 'pathwayChallenge2',
  PATHWAY_CHALLENGE3 = 'pathwayChallenge3',
  PATHWAY_CHALLENGE4 = 'pathwayChallenge4',
  PUZZLE_CHALLENGE1 = 'puzzleChallenge1',
  PUZZLE_CHALLENGE2 = 'puzzleChallenge2',
  PUZZLE_CHALLENGE3 = 'puzzleChallenge3',
  PUZZLE_CHALLENGE4 = 'puzzleChallenge4',
  MEMORY_GAME_CHALLENGE1 = 'memoryGameChallenge1',
  MEMORY_GAME_CHALLENGE2 = 'memoryGameChallenge2',
  MEMORY_GAME_CHALLENGE3 = 'memoryGameChallenge3',
  MEMORY_GAME_CHALLENGE4 = 'memoryGameChallenge4',
  MINESWEEPER_GAME_CHALLENGE1 = 'minesweeperGameChallenge1',
  MINESWEEPER_GAME_CHALLENGE2 = 'minesweeperGameChallenge2',
  MINESWEEPER_GAME_CHALLENGE3 = 'minesweeperGameChallenge3',
  MINESWEEPER_GAME_CHALLENGE4 = 'minesweeperGameChallenge4',
  ONE_YEAR = 'oneYear',
  PARACHUTE1 = 'parachute1',
  PARACHUTE2 = 'parachute2',
  PARACHUTE3 = 'parachute3',
  PARACHUTE4 = 'parachute4',
  HOLIDAY_ORB1 = 'holidayOrb1',
  HOLIDAY_ORB2 = 'holidayOrb2',
  HOLIDAY_ORB3 = 'holidayOrb3',
  HOLIDAY_ORB4 = 'holidayOrb4',
}

export type Badge = {
  id: BADGE_ID
  name: string
  imageUrl: string
}

export enum INVENTORY_ID {
  TGE_ALLOCATION_BOOST = 'tgeAllocationBoost',
  GOLD_TGE_BOOST_CARD = 'goldTgeBoostCard',
  SILVER_TGE_BOOST_CARD = 'silverTgeBoostCard',
  BRONZE_TGE_BOOST_CARD = 'bronzeTgeBoostCard',
  DIAMOND_MULTIPLIER = 'diamondMultiplier',
  PLATINUM_PASS = 'platinumPass',
  EMBER_ELEMENT_SHARD = 'emberElementShard',
  FROST_ELEMENT_SHARD = 'frostElementShard',
  VOLT_ELEMENT_SHARD = 'voltElementShard',
  STONE_ELEMENT_SHARD = 'stoneElementShard',
  ELEMENT_MULTIPLIER = 'elementMultiplier',
  RAINBOW_KEY = 'rainbowKey',
  WINTER_CROWN = 'winterCrown',
  PRIME_SIGIL = 'primeSigil'
}

export type InventoryItem = {
  id: INVENTORY_ID,
  name: string
  imageUrl: string
}

export enum NOTIFICATION_TYPE {
  BADGE = 'badge',
  DAILY_UP_WINNER = 'dailyUpWinner',
  PATHWAY_CHALLENGE_WINNER = 'pathwayChallengeWinner',
  PUZZLE_CHALLENGE_WINNER = 'puzzleChallengeWinner',
  TON_DAILY_CHECKIN = 'tonDailyCheckin',
  DAPP_RADAR_TTM = 'dappRadarTtm',
  MEMORY_GAME_CHALLENGE_WINNER = 'memoryGameChallengeWinner',
  MINESWEEPER_GAME_CHALLENGE_WINNER = 'minesweeperGameChallengeWinner',
  SPECIAL_COLLECTIBLE_WINNER = 'specialCollectibleWinner',
}

type NotificationBase = { timestamp: number, type: NOTIFICATION_TYPE }

export type BadgeNotification = NotificationBase & {
  type: NOTIFICATION_TYPE.BADGE,
  badgeId: BADGE_ID
}

export type TonDailyCheckinNotification = NotificationBase
export type DailyUpWinnerNotification = NotificationBase & {
  position: number,
  date: number,
  amount: number,
  prizeType: CHALLENGE_PRIZE_TYPE,
  specialCurrencyId: SPECIAL_CURRENCY_ID | undefined,
  cardUpgrades: number
  nft?: string,
  lottieUrl?: string
  badgeId: BADGE_ID | undefined
}
export type PathwayChallengeWinnerNotification = NotificationBase & {
  position: number,
  date: number,
  score: number
  prizeType: CHALLENGE_PRIZE_TYPE,
  specialCurrencyId: SPECIAL_CURRENCY_ID | undefined,
  amount?: number,
  nft?: string,
  lottieUrl?: string
  badgeId: BADGE_ID | undefined
}
export type PuzzleChallengeWinnerNotification = NotificationBase & {
  position: number,
  date: number,
  score: number
  prizeType: CHALLENGE_PRIZE_TYPE,
  specialCurrencyId: SPECIAL_CURRENCY_ID | undefined,
  amount?: number,
  nft?: string,
  lottieUrl?: string
  badgeId: BADGE_ID | undefined
}

export type MemoryGameChallengeWinnerNotification = NotificationBase & {
  type: NOTIFICATION_TYPE.MEMORY_GAME_CHALLENGE_WINNER,
  position: number,
  date: number,
  durationMillis: number
  prizeType: CHALLENGE_PRIZE_TYPE,
  specialCurrencyId: SPECIAL_CURRENCY_ID | undefined,
  amount?: number,
  nft?: string,
  lottieUrl?: string
  badgeId: BADGE_ID | undefined
}

export type MinesweeperGameChallengeWinnerNotification = NotificationBase & {
  type: NOTIFICATION_TYPE.MINESWEEPER_GAME_CHALLENGE_WINNER,
  position: number,
  date: number,
  durationMillis: number
  prizeType: CHALLENGE_PRIZE_TYPE,
  specialCurrencyId: SPECIAL_CURRENCY_ID | undefined,
  amount?: number,
  nft?: string,
  lottieUrl?: string
  badgeId: BADGE_ID | undefined
}

export type SpecialCollectibleWinnerNotification = NotificationBase & {
  type: NOTIFICATION_TYPE.SPECIAL_COLLECTIBLE_WINNER,
  position: number,
  date: number,
  score: number,
  prizeType: CHALLENGE_PRIZE_TYPE,
  specialCurrencyId: SPECIAL_CURRENCY_ID | undefined,
  amount?: number,
  nft?: string,
  lottieUrl?: string,
  badgeId: BADGE_ID | undefined,
  currency: SPECIAL_CURRENCY_ID
  challengeName: string | undefined
}

export type Notification =
  BadgeNotification
  | TonDailyCheckinNotification
  | DailyUpWinnerNotification
  | PathwayChallengeWinnerNotification
  | PuzzleChallengeWinnerNotification
  | MemoryGameChallengeWinnerNotification
  | MinesweeperGameChallengeWinnerNotification
  | SpecialCollectibleWinnerNotification

export function isBadgeNotification(notification: Notification): notification is BadgeNotification {
  return notification.type === NOTIFICATION_TYPE.BADGE
}

export function isDailyUpWinnerNotification(notification: Notification): notification is DailyUpWinnerNotification {
  return notification.type === NOTIFICATION_TYPE.DAILY_UP_WINNER
}

export function isPathwayChallengeWinnerNotification(notification: Notification): notification is PathwayChallengeWinnerNotification {
  return notification.type === NOTIFICATION_TYPE.PATHWAY_CHALLENGE_WINNER
}

export function isPuzzleChallengeWinnerNotification(notification: Notification): notification is PuzzleChallengeWinnerNotification {
  return notification.type === NOTIFICATION_TYPE.PUZZLE_CHALLENGE_WINNER
}

export function isMemoryGameChallengeWinnerNotification(notification: Notification): notification is MemoryGameChallengeWinnerNotification {
  return notification.type === NOTIFICATION_TYPE.MEMORY_GAME_CHALLENGE_WINNER
}

export function isMinesweeperGameChallengeWinnerNotification(notification: Notification): notification is MinesweeperGameChallengeWinnerNotification {
  return notification.type === NOTIFICATION_TYPE.MINESWEEPER_GAME_CHALLENGE_WINNER
}

export function isSpecialCollectibleChallengeWinnerNotification(notification: Notification): notification is SpecialCollectibleWinnerNotification {
  return notification.type === NOTIFICATION_TYPE.SPECIAL_COLLECTIBLE_WINNER
}
